import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { fetchCollection } from '../lib/cms';
import { Heart, ShieldCheck, ExternalLink, Sparkles } from 'lucide-react';

export default function SponsorshipsPage() {
  const [sponsorships, setSponsorships] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadSponsorships() {
      const data = await fetchCollection('sponsorships');
      if (data && data.items) {
        // Filtrer "Lokale initiativer (Fleksibel)" fra som anmodet
        const filtered = data.items.filter(
          item => item.slug?.da !== 'lokale-initiativer-fleksibel'
        );
        setSponsorships(filtered);
      }
      setLoading(false);
    }
    loadSponsorships();
  }, []);

  const metaTitle = "Vi støtter - KBS Tømrer – Montage ApS";
  const metaDesc = "Hos KBS Tømrer – Montage ApS er vi stolte af at støtte gode formål som Knæk Cancer, Danske Hospitalsklovne og lokale foreninger i Malling og omegn.";

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDesc} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDesc} />
        <html lang="da" />
      </Helmet>

      {/* Hero Header */}
      <section className="relative bg-slate-950 text-white py-20 lg:py-28">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/10033775-ad6c-49dd-a719-01817fe062a0/baggrund-22.jpg"
            alt="KBS Tømrer Vi støtter"
            className="h-full w-full object-cover object-center opacity-15"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/50 via-slate-950 to-slate-950" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-primary border border-brand-primary/20 px-4 py-1.5 text-xs sm:text-sm font-bold text-white mb-4 uppercase tracking-wider">
            <Heart className="h-4 w-4 fill-current" /> Samfundsansvar & Omtanke
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Vi støtter en god sag
          </h1>
          <p className="mt-4 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Som lokal virksomhed mener vi, det er vigtigt at tage ansvar og give noget tilbage. Vi støtter organisationer, der gør en reel forskel for mennesker i svære situationer.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white flex-1">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          {sponsorships.length === 0 ? (
            <div className="text-center py-12 text-slate-400">
              Der blev ikke fundet nogen sponsorater i CMS'et.
            </div>
          ) : (
            <div className="space-y-16">
              {sponsorships.map((item, index) => {
                const hasLogo = item.logo && item.logo.length > 0;
                const isEven = index % 2 === 0;

                // Optimer logo-billede via Cloudflare resizing (~300px bredde)
                const optimizedLogo = hasLogo
                  ? item.logo[0].replace('https://assets.auxodev.dk/assets/', 'https://assets.auxodev.dk/cdn-cgi/image/width=300,quality=90,format=auto/assets/')
                  : null;

                return (
                  <div
                    key={item.id}
                    className={`flex flex-col md:flex-row items-center gap-8 md:gap-12 p-6 sm:p-8 rounded-2xl border border-slate-100 bg-slate-50/50 shadow-sm hover:shadow-md transition-all ${
                      isEven ? '' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Logo/Image Container */}
                    {item.slug?.da === 'danske-hospitalsklovne' ? (
                      <div className="w-full md:w-1/3 shrink-0 flex justify-center bg-white rounded-xl p-6 border border-slate-100 shadow-inner aspect-[4/3] items-center">
                        <span className="font-display text-xl sm:text-2xl font-extrabold text-[#202524] tracking-tight text-center">
                          Danske Hospitalsklovne
                        </span>
                      </div>
                    ) : hasLogo ? (
                      <div className="w-full md:w-1/3 shrink-0 flex justify-center bg-white rounded-xl p-6 border border-slate-100 shadow-inner aspect-[4/3] items-center">
                        <img
                          src={optimizedLogo}
                          alt={item.title.da}
                          className="max-h-24 md:max-h-32 w-auto object-contain"
                        />
                      </div>
                    ) : (
                      <div className="w-full md:w-1/3 shrink-0 flex justify-center bg-brand-primary/5 rounded-xl p-6 border border-slate-100 aspect-[4/3] items-center text-brand-primary">
                        <Sparkles className="h-16 w-16" />
                      </div>
                    )}

                    {/* Text Content */}
                    <div className="flex-1">
                      <h2 className="font-display text-2xl font-bold text-brand-charcoal mb-4">
                        {item.title.da}
                      </h2>
                      <div
                        className="prose prose-slate text-sm sm:text-base leading-relaxed text-slate-600 mb-6"
                        dangerouslySetInnerHTML={{ __html: item.description.da }}
                      />
                      {item.link && (
                        <a
                          href={item.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-bold text-brand-primary hover:text-brand-primary-dark transition-colors cursor-pointer"
                        >
                          Besøg hjemmeside <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Warm Closing Note */}
          <div className="mt-20 rounded-2xl bg-brand-primary/5 border border-brand-primary/10 p-8 text-center max-w-3xl mx-auto">
            <ShieldCheck className="h-10 w-10 text-brand-primary mx-auto mb-4" />
            <h3 className="font-display text-lg font-bold text-brand-charcoal mb-2">
              Tak til vores kunder
            </h3>
            <p className="text-sm text-slate-600 leading-relaxed">
              Vores støtte til disse formål er kun mulig takket være den tillid, vores kunder viser os hver dag. Når du vælger KBS Tømrer – Montage ApS til dit byggeprojekt, er du indirekte med til at bakke op om disse vigtige sager.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
